import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { MbsPopupType, ModalComponent } from 'mbs-ui-kit';
import { ProblemDetailsObject } from 'mbs-ui-shared@models/ProblemDetailsObject';
import { TFAService } from 'mbs-ui-shared@services';
import { ProviderService } from 'mbs-ui-shared@services/provider.service';
import { mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import OpenCrypto from 'opencrypto';
import { Observable, catchError, of, take } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'mbs-password-recovery-modal-component',
  templateUrl: './password-recovery-modal.component.html'
})
export class PasswordRecoveryModalComponent implements OnInit {
  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef;

  public hid: string;
  public readonly maxLengthRecoveryKeyControl = 6000;
  public readonly mbsPopup = MbsPopupType;
  public readonly elementsSelector = {
    name: {
      recoveryKeyControl: 'password-recovery-modal-key-control',
      errorContainer: 'password-recovery-modal-error-container',
      errorContainerUnknownReason: 'password-recovery-modal-error-container-unknown-reason'
    }
  };

  public keysCreatedDate: string = new Date().toString();
  public dateFormat: string = mediumDateWithTimeMoment;
  public loading = false;
  public recoveryKeyControl: FormControl = new FormControl('', [Validators.required]);
  public gotError = false;
  public passwordCanBeRestored = true;
  public errorDetails: Partial<ProblemDetailsObject>;

  private preDecodedKey: string;
  private crypt = new OpenCrypto();

  constructor(public ability: AbilityService, private providerService: ProviderService, private TFAService: TFAService) {}

  close = (): void => {
    this.baseModal.save();
  };

  dismiss = (): void => {
    this.baseModal.close();
  };

  ngOnInit() {
    this.loading = true;
    const backupPath = this.baseModal.data.path;
    this.hid = this.baseModal.data.hid;

    this.setPasswordRecoveryData(this.hid, backupPath);

    this.recoveryKeyControl.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.gotError = false;
    });
  }

  show2FAConfirm(data): Promise<unknown> {
    return this.TFAService.openApproveModal(data);
  }

  setPasswordRecoveryServiceState(targetState: boolean, publicKey: string, mfaCode: string): Observable<any> {
    return this.providerService
      .setPasswordRecoveryState(targetState, publicKey ? JSON.parse(publicKey) : false, mfaCode)
      .pipe(untilDestroyed(this));
  }

  setPasswordRecoveryData(hid: string, backupPath: string): void {
    this.providerService
      .getPasswordRecoveryData(hid, backupPath)
      .pipe(
        take(1),
        catchError((error) => of(this.handleError(error)))
      )
      .subscribe((res) => {
        if (res.authId) {
          this.show2FAConfirm(res)
            .then(() => this.setPasswordRecoveryData(hid, backupPath))
            .catch(() => this.baseModal.close())
            .finally(() => (this.loading = false));
        } else {
          if (res.data && res.keyTime) {
            this.preDecodedKey = res.data;
            this.keysCreatedDate = moment(res.keyTime).toString();
          } else {
            this.passwordCanBeRestored = false;
          }
          this.loading = false;
        }
      });
  }

  async decrypt(): Promise<string> {
    const val = this.recoveryKeyControl.value;
    let privateKey;
    try {
      privateKey = await this.crypt.pemPrivateToCrypto(val, {
        name: 'RSA-OAEP',
        hash: 'SHA-1',
        usages: ['decrypt'],
        isExtractable: true
      });
    } catch (e) {
      this.gotError = true;
      return new Promise((resolve, reject) => {
        reject(Error());
      });
    }

    const cypherText = b642ab(this.preDecodedKey);

    const bufferSize = 512; // Check modulusLength param in Crypto Key, which generated in EnablePasswordRecoveryWizardComponent

    let offset = 0;
    let unreadedBytesCount = cypherText.byteLength;

    const decodedChunks: Array<Uint8Array> = [];

    while (unreadedBytesCount > 0) {
      const bytesToRead = Math.min(unreadedBytesCount, bufferSize);
      const chunk = cypherText.slice(offset, offset + bytesToRead);

      const decryptedChunk = await crypto.subtle.decrypt(
        {
          name: 'RSA-OAEP'
        },
        privateKey,
        chunk
      );
      decodedChunks.push(new Uint8Array(decryptedChunk));

      offset += bytesToRead;
      unreadedBytesCount -= bytesToRead;
    }

    return new TextDecoder().decode(concatUint8Arrays(decodedChunks));
  }

  handleRecovery(): void {
    this.decrypt()
      .then((pass: string) => {
        this.baseModal.save(pass);
      })
      .catch((err) => {
        this.recoveryKeyControl.setErrors({
          decrypt: {
            message: 'Failed to recover password. The specified recovery key is invalid'
          }
        });
        this.recoveryKeyControl.markAsDirty({ onlySelf: true });
      });
  }

  triggerUploadFile(): void {
    this.fileInput.nativeElement.click();
  }

  handleUploadFile(event): void {
    if (event.target.files && event.target.files[0]) {
      this.readFromFile(event.target.files[0]);
      this.gotError = false;
    }
  }

  private readFromFile(file) {
    const reader = new FileReader();
    reader.addEventListener('load', (event) => {
      const key = atob(event.target.result.toString().split(',')[1]);
      this.recoveryKeyControl.setValue(key, { onlySelf: true, emitEvent: false });
    });
    reader.readAsDataURL(file);
  }

  private handleError(error: unknown & { error: Partial<ProblemDetailsObject> }): unknown {
    return this.handleErrorCase1(error) || (this.errorDetails = error.error);
  }

  private handleErrorCase1(error: unknown & { error: Partial<ProblemDetailsObject> }): boolean {
    return error?.error?.detail?.includes('Password recovery data is not specified for current generation');
  }
}

function concatUint8Arrays(arrays: Array<Uint8Array>) {
  const length = arrays.reduce((len, arr) => len + arr.length, 0);
  const array = new Uint8Array(length);
  let offset = 0;
  for (const arr of arrays) {
    array.set(arr, offset);
    offset += arr.length;
  }
  return array;
}
function b642ab(base64string) {
  return Uint8Array.from(atob(base64string), (c) => c.charCodeAt(0));
}
